import {
    useCallback,
    useContext,
    useEffect,
    useState
} from "react";
import {
    useWindowDimensions,
    TouchableOpacity,
    ScrollView,
    View
} from "react-native";
import type {
    FinanceCategory
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitDialog,
    NCoreUIKitToast,
    NCoreUIKitTheme,
    PageContainer,
    SelectBox,
    TextInput,
    Button,
    Modal,
    Text
} from "ncore-ui-kit";
import {
    RefreshCcwIcon,
    PencilIcon,
    TrashIcon,
    TagIcon
} from "lucide-react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {
    AuthContext
} from "../../contexts/AuthContext";

const FinanceCategories = () => {
    const {
        radiuses,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        activeOrganizationId,
        user
    } = useContext(AuthContext);

    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const [
        categories,
        setCategories
    ] = useState<FinanceCategory[]>([]);

    const [
        modalVisible,
        setModalVisible
    ] = useState(false);

    const [
        isSubmitting,
        setIsSubmitting
    ] = useState(false);

    const [
        isRefreshing,
        setIsRefreshing
    ] = useState(false);

    const [
        formData,
        setFormData
    ] = useState({
        type: "income",
        name: ""
    });

    const [
        editId,
        setEditId
    ] = useState<string | null>(null);

    const fetchCategories = useCallback(async () => {
        if (user?.role === "root" && !activeOrganizationId) {
            setCategories([]);
            return;
        }

        try {
            const token = await AsyncStorage.getItem("authToken");
            const query = activeOrganizationId ? `?organization_id=${activeOrganizationId}` : "";
            const response = await fetch(`${process.env.API_URL}/finance-categories${query}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            const result = await response.json();
            if (result.status === "success") {
                setCategories(result.data);
            }
        } catch (error) {
            console.error(localize("finance-categories-toast-unknown-error"), error);
        }
    }, [
        activeOrganizationId,
        user?.role
    ]);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    const handleSave = async () => {
        if (!formData.name) {
            alert(localize("finance-categories-alert-name-required"));
            return;
        }

        if (user?.role === "root" && !activeOrganizationId) {
            alert(localize("finance-categories-alert-org-required"));
            return;
        }

        if (isSubmitting) {
            return;
        }

        setIsSubmitting(true);

        try {
            const token = await AsyncStorage.getItem("authToken");
            const url = editId
                ? `${process.env.API_URL}/finance-categories/${editId}`
                : `${process.env.API_URL}/finance-categories`;

            const reqBody = activeOrganizationId ? {
                ...formData,
                organization_id: activeOrganizationId
            } : formData;

            const response = await fetch(url, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                method: editId ? "PUT" : "POST",
                body: JSON.stringify(reqBody)
            });

            const result = await response.json();
            if (result.status === "success") {
                setModalVisible(false);
                setEditId(null);
                setFormData({
                    type: "income",
                    name: ""
                });
                fetchCategories();
            } else {
                alert(result.message || (result.errors ? result.errors.map((e: { message: string }) => e.message).join(", ") : localize("finance-categories-toast-unknown-error")));
            }
        } catch (error) {
            console.error("Kayıt hatası:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = (id: string, name: string, force: boolean = false) => {
        let isDeleting = false;

        NCoreUIKitDialog.open({
            primaryButtonProps: {
                onPress: async ({
                    closeAnimation
                }) => {
                    if (isDeleting) return;

                    isDeleting = true;

                    try {
                        const token = await AsyncStorage.getItem("authToken");
                        const url = `${process.env.API_URL}/finance-categories/${id}${force ? "?force=true" : ""}`;
                        const response = await fetch(url, {
                            body: JSON.stringify({
                                organization_id: activeOrganizationId || undefined
                            }),
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${token}`
                            },
                            method: "DELETE"
                        });

                        const result = await response.json();

                        if (response.status === 409) {
                            closeAnimation(() => {
                                setTimeout(() => {
                                    let isForceDeleting = false;

                                    NCoreUIKitDialog.open({
                                        content: localize("finance-categories-dialog-force-content", [
                                            result.message
                                        ]),
                                        primaryButtonProps: {
                                            title: localize("finance-categories-dialog-force-delete"),
                                            onPress: ({
                                                closeAnimation: closeForce
                                            }: {
                                                closeAnimation: (cb?: () => void) => void;
                                            }) => {
                                                if(isForceDeleting) {
                                                    return;
                                                }

                                                isForceDeleting = true;

                                                closeForce(() => handleDelete(id, name, true));
                                            },
                                            type: "danger"
                                        },
                                        title: localize("finance-categories-dialog-confirm-title"),
                                        secondaryButtonProps: {
                                            title: localize("finance-categories-dialog-cancel"),
                                            onPress: ({
                                                closeAnimation: closeForce
                                            }: {
                                                closeAnimation: () => void;
                                            }) => closeForce(),
                                            type: "neutral"
                                        },
                                        variant: "yes-no",
                                        isVisible: true
                                    });
                                }, 300);
                            });
                            return;
                        }

                        if (result.status === "success") {
                            NCoreUIKitToast.open({
                                title: localize("finance-categories-toast-deleted")
                            });
                            fetchCategories();
                        } else {
                            alert(result.message || (result.errors ? result.errors.map((e: { message: string }) => e.message).join(", ") : localize("finance-categories-toast-unknown-error")));
                        }
                    } catch (error) {
                        console.error("Silme hatası:", error);
                    } finally {
                        isDeleting = false;

                        closeAnimation();
                    }
                },
                title: localize("finance-categories-dialog-delete-primary"),
                type: "danger"
            },
            content: localize("finance-categories-dialog-delete-content", [
                name
            ]),
            title: localize("finance-categories-dialog-delete-title"),
            secondaryButtonProps: {
                title: localize("finance-categories-dialog-cancel"),
                onPress: ({
                    closeAnimation
                }) => closeAnimation(),
                type: "neutral"
            },
            variant: "yes-no",
            isVisible: true
        });
    };

    const typeOptions = [
        {
            __title: localize("finance-categories-type-income"),
            __key: "income"
        },
        {
            __title: localize("finance-categories-type-expense"),
            __key: "expense"
        }
    ];

    return <PageContainer
        isScrollable={true}
    >
        <View
            style={[
                stylesheet.container,
                {
                    padding: spaces.spacingXl
                }
            ]}
        >
            <View
                style={[
                    stylesheet.headerRow,
                    {
                        alignItems: isMobile ? "stretch" : "center",
                        flexDirection: isMobile ? "column" : "row",
                        gap: isMobile ? spaces.spacingMd : 0,
                        marginBottom: spaces.spacingXl
                    }
                ]}
            >
                <Text
                    variant="titleLargeSize"
                >
                    {localize("finance-categories-page-title")}
                </Text>
                <View
                    style={{
                        flexDirection: isMobile ? "column" : "row",
                        gap: spaces.spacingMd
                    }}
                >
                    <Button
                        icon={({
                            color,
                            size
                        }) => {
                            return <RefreshCcwIcon
                                color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                size={size}
                            />;
                        }}
                        spreadBehaviour={isMobile ? "stretch" : "free"}
                        onPress={async () => {
                            setIsRefreshing(true);
                            await fetchCategories();
                            setIsRefreshing(false);
                        }}
                        isLoading={isRefreshing}
                        variant="ghost"
                        type="neutral"
                    />
                    {(!user || user.role !== "root" || activeOrganizationId) && (
                        <Button
                            title={localize("finance-categories-page-add-button")}
                            spreadBehaviour={isMobile ? "stretch" : "free"}
                            onPress={() => setModalVisible(true)}
                        />)}
                </View>
            </View>
            <ScrollView
                contentContainerStyle={[
                    stylesheet.listContent,
                    {
                        paddingBottom: spaces.spacingXl,
                        gap: spaces.spacingMd
                    }
                ]}
                showsVerticalScrollIndicator={false}
            >
                {categories.map((item) => {
                    return <TouchableOpacity
                        style={[
                            stylesheet.itemContainer,
                            {
                                backgroundColor: colors.content.container.mid,
                                alignItems: isMobile ? "stretch" : "center",
                                flexDirection: isMobile ? "column" : "row",
                                borderRadius: radiuses.md,
                                padding: spaces.spacingMd,
                                gap: spaces.spacingMd
                            }
                        ]}
                        activeOpacity={0.7}
                        key={item.id}
                    >
                        <View
                            style={[
                                stylesheet.itemContent,
                                {
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <View
                                style={[
                                    stylesheet.itemIcon,
                                    {
                                        backgroundColor: colors.content.container.default,
                                        borderRadius: radiuses.full
                                    }
                                ]}
                            >
                                <TagIcon
                                    color={colors.content.icon.emphasized}
                                    size={24}
                                />
                            </View>
                            <View
                                style={[
                                    stylesheet.itemInfo,
                                    {
                                        gap: spaces.spacingXs
                                    }
                                ]}
                            >
                                <Text
                                    variant="titleMediumSize"
                                >
                                    {item.name}
                                </Text>
                                <Text
                                    color={item.type === "income" ? "success" : "danger"}
                                    variant="bodyMediumSize"
                                >
                                    {item.type === "income" ? localize("finance-categories-list-income-type") : localize("finance-categories-list-expense-type")}
                                </Text>
                            </View>
                        </View>
                        <View
                            style={[
                                stylesheet.itemActions,
                                {
                                    alignItems: isMobile ? "stretch" : "center",
                                    flexDirection: isMobile ? "column" : "row",
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <Button
                                icon={({
                                    color,
                                    size
                                }) => {
                                    return <PencilIcon
                                        color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                        size={size}
                                    />;
                                }}
                                title={localize("finance-categories-list-edit")}
                                spreadBehaviour={isMobile ? "stretch" : "free"}
                                onPress={() => {
                                    setEditId(item.id);
                                    setFormData({
                                        type: item.type,
                                        name: item.name
                                    });
                                    setModalVisible(true);
                                }}
                                type="neutral"
                            />
                            <Button
                                icon={({
                                    color,
                                    size
                                }) => {
                                    return <TrashIcon
                                        color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                        size={size}
                                    />;
                                }}
                                title={localize("finance-categories-list-delete")}
                                onPress={() => handleDelete(item.id, item.name)}
                                spreadBehaviour={isMobile ? "stretch" : "free"}
                                type="danger"
                            />
                        </View>
                    </TouchableOpacity>;
                })}
            </ScrollView>
            <Modal
                id="finance-categories-modal"
                onOverlayPress={({
                    closeAnimation
                }) => closeAnimation(() => {
                    setModalVisible(false);
                    setEditId(null);
                    setFormData({
                        type: "income",
                        name: ""
                    });
                })}
                onClosed={() => {
                    setModalVisible(false);
                    setEditId(null);
                    setFormData({
                        type: "income",
                        name: ""
                    });
                }}
                portalName="modal-system"
                isActive={modalVisible}
            >
                <View
                    style={[
                        stylesheet.modalContent,
                        {
                            backgroundColor: colors.content.container.default,
                            width: isMobile ? "100%" : 500,
                            borderRadius: radiuses.md,
                            padding: spaces.spacingXl
                        }
                    ]}
                >
                    <Text
                        variant="titleLargeSize"
                    >
                        {editId ? localize("finance-categories-modal-edit-title") : localize("finance-categories-modal-add-title")}
                    </Text>
                    <View
                        style={[
                            stylesheet.formContainer,
                            {
                                marginTop: spaces.spacingMd,
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <TextInput
                            placeholder={localize("finance-categories-modal-name-placeholder")}
                            title={localize("finance-categories-modal-name-title")}
                            onChangeText={(text: string) => setFormData(prev => ({
                                ...prev,
                                name: text
                            }))}
                            defaultValue={formData.name}
                            spreadBehaviour="stretch"
                        />
                        <View>
                            <SelectBox
                                initialSelectedItems={typeOptions.filter(t => t.__key === formData.type)}
                                placeholder={localize("finance-categories-modal-type-placeholder")}
                                onChange={(selected) => setFormData(prev => ({
                                    ...prev,
                                    type: (selected[0]?.__key as "income" | "expense") || "income"
                                }))}
                                key={`selectbox-${modalVisible ? "visible" : "hidden"}`}
                                title={localize("finance-categories-modal-type-label")}
                                titleExtractor={(item) => item.__title}
                                keyExtractor={(item) => item.__key}
                                selectSheetProps={{
                                    portalName: "modal-system"
                                }}
                                spreadBehaviour="stretch"
                                data={typeOptions}
                            />
                        </View>
                    </View>
                    <View
                        style={[
                            stylesheet.modalActions,
                            {
                                flexDirection: isMobile ? "column-reverse" : "row",
                                marginTop: spaces.spacingXl,
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <Button
                            title={localize("finance-categories-modal-cancel")}
                            onPress={() => {
                                setModalVisible(false);
                                setEditId(null);
                                setFormData({
                                    type: "income",
                                    name: ""
                                });
                            }}
                            spreadBehaviour="stretch"
                            variant="filled"
                            type="neutral"
                        />
                        <Button
                            title={localize("finance-categories-modal-save")}
                            spreadBehaviour="stretch"
                            isLoading={isSubmitting}
                            onPress={handleSave}
                            type="primary"
                        />
                    </View>
                </View>
            </Modal>
        </View>
    </PageContainer>;
};
export default FinanceCategories;
